<template>
  <div class="phone-number-container bsp-page">
    <div class="phone-number-loaded bsp-page__content" v-if="!loading && phoneNumber">
      <BspPageHeader
        eyebrow="Phone number settings"
        title="Message templates"
        description="View, test, and manage the templates available for this phone number."
        icon="tabler:message-code"
        :breadcrumbs="templateBreadcrumbs"
      >
        <template #actions>
          <AButton
            color="blue"
            variant="outlined"
            prepend-icon="tabler:refresh"
            @click="synchronizeMessageTemplates"
          >
            Synchronize
          </AButton>
        </template>
      </BspPageHeader>
      <section v-if="isCreatingCatalogTemplate || isTestingCatalogTemplate || hasCatalogTemplates" class="bsp-section bsp-catalog-template-notice" aria-label="Connected product catalog">
        <div>
          <h2>Connected product catalog</h2>
          <p v-if="catalogsLoading" role="status">Checking the WABA catalog…</p>
          <p v-else-if="catalogsError" role="alert">{{ catalogsError }}</p>
          <p v-else-if="associatedCatalog">
            {{ associatedCatalog.name }} · {{ associatedCatalog.product_count ?? 'Unknown number of' }} products.
            <span v-if="associatedCatalog.product_count === 0">Add products in Meta Commerce Manager before creating or sending a catalog template.</span>
            <span v-else>The template opens this WABA's catalog. Products must be approved by Meta.</span>
          </p>
          <p v-else>Connect a catalog with products to this WABA before creating or sending a catalog template.</p>
          <p v-if="visibilityLoading || visibilitySaving" role="status">Checking catalog visibility for this WhatsApp number…</p>
          <p v-else-if="visibilityError" role="alert">Catalog visibility could not be confirmed. Refresh visibility before sending a catalog template. {{ visibilityError }}</p>
          <p v-else-if="catalogVisible === false" role="alert">The catalog is hidden for this WhatsApp number. Catalog templates cannot be sent until you enable visibility in Manage catalogs.</p>
          <p v-else-if="catalogVisible === true">The catalog is visible for this WhatsApp number. Meta must also approve the template and its products.</p>
          <p v-else role="status">Catalog visibility has not been confirmed for this WhatsApp number.</p>
        </div>
        <div class="bsp-component-actions">
          <AButton size="small" variant="outlined" :loading="catalogsLoading || visibilityLoading" :disabled="visibilitySaving" @click="refreshCatalogReadiness">Refresh catalog and visibility</AButton>
          <AButton size="small" variant="text" prepend-icon="tabler:shopping-bag" @click="router.push({ name: 'product-catalogs', params: { wabaId: String(route.params.wabaId) }, query: { phoneNumberId: String(route.params.phoneNumberId) } })">Manage catalogs</AButton>
        </div>
      </section>
      <div class="message-templates-and-creation">
        <div class="message-templates bsp-section">
          <div class="phone-number">
            <div class="message-template-container">
              <div class="bsp-section-heading">
                <div>
                  <h2>Available templates</h2>
                  <p>Select a template to prepare a test message.</p>
                </div>
                <ATag
                  :text="`${filteredMessageTemplates.length} ${filteredMessageTemplates.length === 1 ? 'template' : 'templates'}`"
                  color="primary"
                  type="secondary"
                  pill
                />
              </div>
              <AInput
                v-model="templateSearch"
                class="bsp-search-input"
                label="Search templates"
                placeholder="Name, language, or status"
                prepend-inner-icon="tabler:search"
                clearable
              />
              <div class="message-template-list" v-if="filteredMessageTemplates.length > 0">
                <div class="message-template" :class="{ 'selected': testingMessageTemplate && messageTemplate.name == testingMessageTemplate.name }" @click="selectTestTemplate(messageTemplate)" v-for="messageTemplate in filteredMessageTemplates" v-bind:key="messageTemplate.id">
                  <div class="message-template-name">
                    {{ messageTemplate.name }}
                  </div>
                  <div class="message-template-status">
                    <ATag :text="messageTemplate.language" color="info" type="secondary" pill />
                    <ATag
                      :text="messageTemplate.status"
                      :color="messageTemplate.status === 'APPROVED' ? 'success' : messageTemplate.status === 'REJECTED' ? 'error' : 'warning'"
                      type="secondary"
                      pill
                    />
                    <ATag v-if="isCatalogTemplate(messageTemplate) && catalogVisible === false"
                      text="Catalog hidden for this number" color="warning" type="secondary" pill />
                    <p v-if="messageTemplate.rejected_reason !== 'NONE'">
                      {{ messageTemplate.rejected_reason || '' }}
                    </p>
                  </div>
                  <div class="message-template-actions">
                    <AButton
                      color="red"
                      variant="text"
                      size="small"
                      prepend-icon="tabler:trash"
                      @click.stop="deleteMessageTemplate(messageTemplate)"
                    >
                      Delete
                    </AButton>
                  </div>
                </div>
              </div>
              <AEmptyViewCard
                v-else
                title="No templates found"
                :description="templateSearch ? 'Try another search.' : 'Synchronize with Meta to load the available templates.'"
                icon="tabler:message-off"
              />
            </div>
            <section class="bsp-template-test" v-if="testingMessageTemplate" aria-labelledby="template-test-title">
              <div class="bsp-template-test__heading">
                <div>
                  <span class="bsp-section-heading__eyebrow">Controlled message</span>
                  <h3 id="template-test-title">Test template</h3>
                  <p>Complete the variables and review the message before sending it.</p>
                </div>
                <ATag
                  :text="testingMessageTemplate.name"
                  color="primary"
                  type="secondary"
                  prepend-icon="tabler:message-code"
                  :limited-width="240"
                  pill
                />
              </div>

              <div class="bsp-template-test__workspace">
                <div class="bsp-template-preview-panel">
                  <div class="bsp-template-panel-heading">
                    <AIcon icon="tabler:message-circle" color="primary" size="18" />
                    <div>
                      <h4>Message preview</h4>
                      <p>This is how the main content will appear in WhatsApp.</p>
                    </div>
                  </div>

                  <div class="bsp-whatsapp-preview">
                    <div class="bsp-whatsapp-preview__brand">
                      <AIcon icon="tabler:brand-whatsapp" color="success" size="16" />
                      <span>Template message</span>
                    </div>
                    <div class="bsp-whatsapp-preview__bubble">
                      <template v-if="isTestingCatalogTemplate">
                        <div class="bsp-template-preview__header">
                          <img v-if="catalogThumbnailImageUrl && !catalogImageFailed" :src="catalogThumbnailImageUrl"
                            :alt="thumbnailProduct?.name || 'Catalog product thumbnail'" referrerpolicy="no-referrer"
                            @error="catalogImageFailed = true" />
                          <div v-else class="bsp-template-media-placeholder" role="status">
                            <AIcon icon="tabler:shopping-bag" color="primary" size="22" />
                            <span>{{ thumbnailLoading ? 'Loading product image…' : thumbnailError || (catalogImageFailed ? 'Image unavailable in this browser' : 'No usable product image returned') }}</span>
                          </div>
                        </div>
                        <div class="bsp-template-catalog-summary">
                          <strong>{{ catalogCardLabels.title }}</strong>
                          <p>{{ catalogCardLabels.subtitle }}</p>
                        </div>
                      </template>
                      <div
                        v-for="(component, componentIndex) in testingMessageTemplate.components"
                        :key="componentIndex"
                        class="bsp-template-preview__component"
                      >
                        <div class="bsp-template-preview__header" v-if="isWhatsappHeaderComponent(component)">
                          <template v-if="component.parameters?.length && isImageHeaderParameter(component.parameters[0])">
                            <img
                              v-show="!failedToLoadImage"
                              :src="component.parameters[0].image.link"
                              @error="failedToLoadImage = true"
                              @load="failedToLoadImage = false"
                              alt="Header preview"
                            />
                            <div v-show="failedToLoadImage" class="bsp-template-media-placeholder">
                              <AIcon icon="tabler:photo-off" color="info" size="22" />
                              <span>Unable to load the image</span>
                            </div>
                          </template>
                          <div
                            v-else-if="component.format && component.format !== 'TEXT'"
                            class="bsp-template-media-placeholder"
                          >
                            <AIcon icon="tabler:paperclip" color="info" size="22" />
                            <span>{{ component.format.toLowerCase() }} header</span>
                          </div>
                          <strong v-else-if="component.text">{{ interpolateTemplateText(component.text, component.parameters) }}</strong>
                        </div>
                        <p class="bsp-template-preview__body" v-if="component.type === 'body'">
                          {{ interpolateTemplateText(component.text, component.parameters) }}
                        </p>
                        <small class="bsp-template-preview__footer" v-if="component.type === 'footer'">
                          {{ component.text }}
                        </small>
                        <div class="bsp-template-preview__button" v-if="component.type === 'button'">
                          <AIcon
                            :icon="component.sub_type === 'CATALOG' ? 'tabler:shopping-bag' : component.sub_type === 'phone_number' ? 'tabler:phone' : component.sub_type === 'url' ? 'tabler:external-link' : component.sub_type === 'copy_code' ? 'tabler:copy' : 'tabler:corner-up-left'"
                            color="primary"
                            size="15"
                          />
                          <span>
                            {{ component.sub_type === 'CATALOG' ? catalogCardLabels.button : component.text || (component.sub_type === 'phone_number' ? 'Call' : component.sub_type === 'url' ? 'Open link' : component.sub_type === 'copy_code' ? 'Copy code' : 'Reply') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="isTestingCatalogTemplate" class="bsp-template-variable-group">
                    <h5>Catalog thumbnail</h5>
                    <ASelector v-model="selectedThumbnailSku" size="small" label="Thumbnail product"
                      :items="thumbnailProductOptions" item-title="title" item-value="value" autocomplete
                      placeholder="Search by product name or SKU" no-data-text="No matching products"
                      :loading="thumbnailLoading" :disabled="sendingTest || thumbnailLoading || !thumbnailProductOptions.length" />
                    <p class="bsp-template-field-caption">Choose a product from the connected catalog. The test message will use the same product shown in this preview.</p>
                    <p v-if="thumbnailNextCursor" class="bsp-template-field-caption">More products are available. Load more to include them in your search.</p>
                    <p v-if="thumbnailProduct" class="bsp-template-field-caption">Preview product: {{ thumbnailProduct.name || thumbnailProduct.retailer_id }} · SKU {{ thumbnailProduct.retailer_id }}</p>
                    <p v-if="thumbnailError" role="alert">{{ thumbnailError }}</p>
                    <p v-if="thumbnailLoadMoreError" role="alert">{{ thumbnailLoadMoreError }}</p>
                    <div class="bsp-component-actions">
                      <AButton v-if="thumbnailNextCursor" variant="outlined" size="small" :loading="thumbnailLoadingMore"
                        :disabled="sendingTest || thumbnailLoading || thumbnailLoadingMore" @click="loadMoreThumbnailProducts">Load more products</AButton>
                      <AButton variant="text" size="small" :disabled="sendingTest || thumbnailLoading || thumbnailLoadingMore" @click="refreshThumbnail">Refresh products</AButton>
                    </div>
                    <p class="bsp-template-field-caption">WhatsApp controls the final rendering and translations.</p>
                    <p v-if="selectedTestTemplate?.status !== 'APPROVED'" role="alert">Meta must approve this catalog template before it can be sent.</p>
                  </div>
                </div>

                <div class="bsp-template-variables-panel">
                  <div class="bsp-template-panel-heading">
                    <AIcon icon="tabler:braces" color="primary" size="18" />
                    <div>
                      <h4>Message variables</h4>
                      <p>The values you enter replace the template placeholders.</p>
                    </div>
                  </div>

                  <div v-if="testTemplateParameterCount === 0" class="bsp-template-no-variables">
                    <AIcon icon="tabler:circle-check" color="success" size="18" />
                    This template doesn't require variables.
                  </div>

                  <div
                    v-for="(component, componentIndex) in testingMessageTemplate.components"
                    :key="`parameters-${componentIndex}`"
                    class="bsp-template-variable-component"
                  >
                    <div class="bsp-template-variable-group" v-if="isWhatsappHeaderComponent(component) && component.parameters?.length">
                      <h5>Header</h5>
                      <template v-if="isTextHeaderParameter(component.parameters[0])">
                        <AInput
                          v-for="(parameter, parameterIndex) in (component.parameters as TextHeaderParameter[])"
                          :key="parameter.id || parameterIndex"
                          v-model="parameter.text"
                          size="small"
                          :label="`Variable {{${parameterIndex + 1}}}`"
                          placeholder="Enter a value"
                        />
                      </template>
                      <div v-else-if="isImageHeaderParameter(component.parameters[0])" class="bsp-template-variable-stack">
                        <AInput v-model="component.parameters[0].image.link" size="small" label="Image URL" placeholder="https://..." />
                        <AButton
                          variant="text"
                          size="small"
                          prepend-icon="tabler:restore"
                          @click="component.parameters[0].image.link = component.parameters[0].image.default || ''"
                        >
                          Restore original URL
                        </AButton>
                      </div>
                      <div v-else-if="isVideoHeaderParameter(component.parameters[0])" class="bsp-template-variable-stack">
                        <AInput v-model="component.parameters[0].video.link" size="small" label="Video URL" placeholder="https://..." />
                        <AButton
                          variant="text"
                          size="small"
                          prepend-icon="tabler:restore"
                          @click="component.parameters[0].video.link = component.parameters[0].video.default || ''"
                        >
                          Restore original URL
                        </AButton>
                      </div>
                      <div v-else-if="isDocumentHeaderParameter(component.parameters[0])" class="bsp-template-variable-stack">
                        <AInput v-model="component.parameters[0].document.link" size="small" label="Document URL" placeholder="https://..." />
                        <AInput v-model="component.parameters[0].document.filename" size="small" label="File name" placeholder="document.pdf" />
                        <AButton
                          variant="text"
                          size="small"
                          prepend-icon="tabler:restore"
                          @click="component.parameters[0].document.link = component.parameters[0].document.default || ''"
                        >
                          Restore original URL
                        </AButton>
                      </div>
                      <div v-else-if="isLocationHeaderParameter(component.parameters[0])" class="bsp-template-location-grid">
                        <AInput v-model="component.parameters[0].location.latitude" size="small" label="Latitude" />
                        <AInput v-model="component.parameters[0].location.longitude" size="small" label="Longitude" />
                        <AInput v-model="component.parameters[0].location.name" size="small" label="Name" />
                        <AInput v-model="component.parameters[0].location.address" size="small" label="Address" />
                      </div>
                    </div>

                    <div class="bsp-template-variable-group" v-if="component.type === 'body' && component.parameters?.length">
                      <h5>Message body</h5>
                      <AInput
                        v-for="(parameter, parameterIndex) in component.parameters"
                        :key="parameterIndex"
                        v-model="parameter.text"
                        size="small"
                        :label="`Variable {{${parameterIndex + 1}}}`"
                        placeholder="Enter a value"
                      />
                    </div>

                    <div class="bsp-template-variable-group" v-if="component.type === 'button' && component.parameters?.length">
                      <h5>Button action</h5>
                      <AInput
                        v-if="component.sub_type === 'quick_reply'"
                        v-model="component.parameters[0].payload"
                        size="small"
                        label="Reply payload"
                        placeholder="Payload"
                      />
                      <AInput
                        v-if="component.sub_type === 'phone_number'"
                        v-model="component.parameters[0].text"
                        size="small"
                        label="Phone number"
                        placeholder="569..."
                      />
                      <AInput
                        v-if="component.sub_type === 'url'"
                        v-model="component.parameters[0].text"
                        size="small"
                        label="URL suffix"
                        placeholder="value"
                      />
                      <AInput
                        v-if="component.sub_type === 'copy_code'"
                        v-model="component.parameters[0].coupon_code"
                        size="small"
                        label="Code"
                        placeholder="Code"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="bsp-template-recipient">
                <span class="bsp-template-recipient__icon" aria-hidden="true">
                  <AIcon icon="tabler:device-mobile-message" color="primary" size="22" />
                </span>
                <div class="bsp-template-recipient__copy">
                  <h4>Recipient phone number</h4>
                  <p>Use international format without spaces or the “+” symbol.</p>
                </div>
                <AInput
                  v-model="testPhoneNumber"
                  class="bsp-template-recipient__input"
                  type="tel"
                  size="small"
                  label="Test phone number"
                  placeholder="56912345678"
                  prepend-inner-icon="tabler:phone"
                />
                <AButton
                  color="blue"
                  prepend-icon="tabler:send"
                  :disabled="!canSendTest"
                  :loading="sendingTest"
                  @click="testMessageTemplate"
                >
                  Send test
                </AButton>
              </div>
            </section>
          </div>
        </div>
        <div class="message-template-creator bsp-section">
          <div class="bsp-section-heading">
            <div>
              <h2>New template</h2>
              <p>Define the content you'll submit to Meta for review.</p>
            </div>
          </div>
          <div class="message-template-creation-form">
            <ASelector
              v-model="messageTemplateType"
              :items="templateTypeOptions"
              item-title="title"
              item-value="value"
              label="Template type"
              @update:model-value="onTemplateTypeChange"
            />
            <p v-if="isCreatingCatalogTemplate" class="bsp-template-component-note">
              Catalog templates use Marketing, a message body, an optional footer, and a fixed View catalog button. Switching to catalog removes custom headers and other buttons.
            </p>
            <AInput v-model="messageTemplateName" label="Name" placeholder="my_template" />
            <ASelector
              v-model="messageTemplateLanguage"
              :items="languageOptions"
              item-title="title"
              item-value="value"
              label="Language"
              placeholder="Select a language"
            />
            <ASelector
              v-model="messageTemplateCategory"
              :items="categoryOptions"
              item-title="title"
              item-value="value"
              label="Category"
              placeholder="Select a category"
              :disabled="isCreatingCatalogTemplate"
            />
            <span class="bsp-form-label">Components</span>
            <div class="message-template-components">
              <div class="message-template-component-header" v-if="headerComponent">
                <div class="bsp-template-component-heading">
                  <div class="bsp-template-component-heading__copy">
                    <span class="bsp-template-component-icon" aria-hidden="true">
                      <AIcon icon="tabler:layout-navbar" color="primary" size="18" />
                    </span>
                    <div>
                      <h3>Header</h3>
                      <p>Optional content that appears before the message.</p>
                    </div>
                  </div>
                  <AButton
                    v-if="!isCreatingCatalogTemplate"
                    icon="tabler:x"
                    variant="text"
                    size="small"
                    help-text="Remove header"
                    aria-label="Remove header"
                    @click="removeHeaderComponent"
                  />
                </div>
                <ASelector
                  v-model="headerComponent.format"
                  :items="headerFormatOptions"
                  item-title="title"
                  item-value="value"
                  label="Format"
                  size="small"
                />
                <AInput
                  v-if="headerComponent.format === 'TEXT'"
                  v-model="headerComponent.text"
                  type="textarea"
                  :rows="3"
                  size="small"
                  label="Header text"
                  placeholder="Write the header"
                  @update:model-value="headerComponent && onHeaderTextInput(headerComponent)"
                />
                <div v-if="headerComponent.format === 'TEXT' && headerComponent.example" class="bsp-template-parameter-list">
                  <span class="bsp-template-field-caption">Variable examples</span>
                  <AInput
                    v-for="(parameter, parameterIndex) in headerComponent.example.header_text"
                    :key="parameterIndex"
                    v-model="headerComponent.example.header_text[parameterIndex]"
                    size="small"
                    :label="`Variable {{${parameterIndex + 1}}}`"
                    placeholder="Example value"
                  />
                </div>
                <AInput
                  v-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format)"
                  v-model="headerComponent.text"
                  type="url"
                  size="small"
                  label="Sample file URL"
                  placeholder="https://..."
                  prepend-inner-icon="tabler:link"
                />
                <div v-if="headerComponent.format === 'LOCATION'" class="bsp-template-component-note">
                  <AIcon icon="tabler:map-pin" color="info" size="17" />
                  The location will be completed when preparing the message.
                </div>
              </div>

              <div class="message-template-component-body" v-if="bodyComponent">
                <div class="bsp-template-component-heading">
                  <div class="bsp-template-component-heading__copy">
                    <span class="bsp-template-component-icon" aria-hidden="true">
                      <AIcon icon="tabler:text-caption" color="primary" size="18" />
                    </span>
                    <div>
                      <h3>Body</h3>
                      <p>Main content the recipient will receive.</p>
                    </div>
                  </div>
                  <AButton
                    v-if="!isCreatingCatalogTemplate"
                    icon="tabler:x"
                    variant="text"
                    size="small"
                    help-text="Remove body"
                    aria-label="Remove body"
                    @click="removeBodyComponent"
                  />
                </div>
                <AInput
                  v-model="bodyComponent.text"
                  type="textarea"
                  :rows="4"
                  size="small"
                  label="Message"
                  placeholder="Write the template content"
                  @update:model-value="bodyComponent && onBodyTextInput(bodyComponent)"
                />
                <div v-if="bodyComponent.example" class="bsp-template-parameter-list">
                  <span class="bsp-template-field-caption">Variable examples</span>
                  <AInput
                    v-for="(parameter, parameterIndex) in bodyComponent.example.body_text[0]"
                    :key="parameterIndex"
                    v-model="bodyComponent.example.body_text[0][parameterIndex]"
                    size="small"
                    :label="`Variable {{${parameterIndex + 1}}}`"
                    placeholder="Example value"
                  />
                </div>
              </div>

              <div class="message-template-component-button" v-if="buttonsComponent">
                <div class="bsp-template-component-heading">
                  <div class="bsp-template-component-heading__copy">
                    <span class="bsp-template-component-icon" aria-hidden="true">
                      <AIcon icon="tabler:click" color="primary" size="18" />
                    </span>
                    <div>
                      <h3>Buttons</h3>
                      <p>{{ isCreatingCatalogTemplate ? 'Opens the connected WABA catalog.' : 'Optional actions available below the message.' }}</p>
                    </div>
                  </div>
                  <AButton
                    v-if="!isCreatingCatalogTemplate"
                    icon="tabler:x"
                    variant="text"
                    size="small"
                    help-text="Remove buttons"
                    aria-label="Remove buttons"
                    @click="removeButtonsComponent"
                  />
                </div>

                <div v-if="isCreatingCatalogTemplate" class="bsp-template-component-note">
                  <AIcon icon="tabler:shopping-bag" color="primary" size="18" />
                  View catalog · WhatsApp translates the fixed button label for the recipient.
                </div>
                <div class="bsp-template-button-list" v-else-if="buttonsComponent.buttons.length">
                  <div
                    class="bsp-template-button-editor"
                    v-for="(button, buttonIndex) in buttonsComponent.buttons"
                    :key="buttonIndex"
                  >
                    <div class="bsp-template-button-editor__heading">
                      <ATag :text="buttonTypeLabel(button.type)" color="info" type="secondary" pill />
                      <AButton
                        icon="tabler:trash"
                        color="red"
                        variant="text"
                        size="small"
                        help-text="Delete button"
                        aria-label="Delete button"
                        @click="removeTemplateButton(buttonIndex)"
                      />
                    </div>
                    <AInput
                      v-if="button.type === 'QUICK_REPLY'"
                      v-model="button.text"
                      size="small"
                      label="Button text"
                      placeholder="E.g. View options"
                    />
                    <template v-if="button.type === 'PHONE_NUMBER'">
                      <AInput v-model="button.text" size="small" label="Button text" placeholder="E.g. Call" />
                      <AInput v-model="button.phone_number" type="tel" size="small" label="Phone number" placeholder="56912345678" />
                    </template>
                    <template v-if="button.type === 'URL'">
                      <AInput v-model="button.text" size="small" label="Button text" placeholder="E.g. Open website" />
                      <AInput
                        v-model="button.url"
                        type="url"
                        size="small"
                        label="URL"
                        placeholder="https://..."
                        @update:model-value="onUrlButtonUrlInput(button)"
                      />
                      <AInput
                        v-for="(parameter, parameterIndex) in button.example"
                        :key="parameterIndex"
                        v-model="button.example[parameterIndex]"
                        size="small"
                        :label="`URL variable {{${parameterIndex + 1}}}`"
                        placeholder="Example value"
                      />
                    </template>
                    <template v-if="button.type === 'COPY_CODE'">
                      <AInput v-model="button.text" size="small" label="Button text" placeholder="Copy code" />
                      <AInput v-model="button.example[0]" size="small" label="Sample code" placeholder="PROMO2026" />
                    </template>
                  </div>
                </div>
                <div v-else class="bsp-template-component-note">
                  <AIcon icon="tabler:info-circle" color="info" size="17" />
                  Add at least one button type using the options below.
                </div>
                <div v-if="!isCreatingCatalogTemplate" class="bsp-component-actions bsp-component-actions--buttons">
                  <AButton size="small" variant="outlined" prepend-icon="tabler:message-reply" @click="addQuickReplyButton(buttonsComponent)">Quick reply</AButton>
                  <AButton size="small" variant="outlined" prepend-icon="tabler:phone" @click="addPhoneNumberButton(buttonsComponent)">Phone number</AButton>
                  <AButton size="small" variant="outlined" prepend-icon="tabler:external-link" @click="addUrlrButton(buttonsComponent)">URL</AButton>
                  <AButton size="small" variant="outlined" prepend-icon="tabler:copy" @click="addCopyCodeButton(buttonsComponent)">Copy code</AButton>
                </div>
              </div>

              <div class="message-template-component-footer" v-if="footerComponent">
                <div class="bsp-template-component-heading">
                  <div class="bsp-template-component-heading__copy">
                    <span class="bsp-template-component-icon" aria-hidden="true">
                      <AIcon icon="tabler:layout-bottombar" color="primary" size="18" />
                    </span>
                    <div>
                      <h3>Footer</h3>
                      <p>Short supporting text at the end of the template.</p>
                    </div>
                  </div>
                  <AButton
                    icon="tabler:x"
                    variant="text"
                    size="small"
                    help-text="Remove footer"
                    aria-label="Remove footer"
                    @click="removeFooterComponent"
                  />
                </div>
                <AInput
                  v-model="footerComponent.text"
                  type="textarea"
                  :rows="2"
                  size="small"
                  label="Message footer"
                  placeholder="E.g. Thank you for choosing us"
                />
              </div>
            </div>

            <div class="bsp-template-component-picker">
              <div>
                <h3>Add component</h3>
                <p>{{ isCreatingCatalogTemplate ? 'Add an optional footer to the catalog message.' : 'You can remove it and add it again without losing the rest of the form.' }}</p>
              </div>
              <div class="bsp-component-actions">
                <AButton size="small" variant="outlined" prepend-icon="tabler:layout-navbar" @click="addHeaderComponent" v-if="!isCreatingCatalogTemplate && headerComponent === undefined">Header</AButton>
                <AButton size="small" variant="outlined" prepend-icon="tabler:text-caption" @click="addBodyComponent" v-if="bodyComponent === undefined">Body</AButton>
                <AButton size="small" variant="outlined" prepend-icon="tabler:layout-bottombar" @click="addFooterComponent" v-if="footerComponent === undefined">Footer</AButton>
                <AButton size="small" variant="outlined" prepend-icon="tabler:click" @click="addButtonsComponent" v-if="!isCreatingCatalogTemplate && buttonsComponent === undefined">Buttons</AButton>
                <span v-if="headerComponent && bodyComponent && footerComponent && buttonsComponent" class="bsp-template-components-complete">
                  <AIcon icon="tabler:circle-check" color="success" size="16" />
                  All components have been added
                </span>
              </div>
            </div>
            <p v-if="isCreatingCatalogTemplate && catalogCreationError" class="bsp-template-component-note" role="status">{{ catalogCreationError }}</p>
            <AButton color="blue" block prepend-icon="tabler:send" @click="createMessageTemplate" :disabled="creatingTemplate || !validTemplate()" :loading="creatingTemplate">
              Create and submit for review
            </AButton>
          </div>
        </div>
      </div>
    </div>
    <SpinnerLoader v-else/>
  </div>
</template>

<script setup lang="ts">

import { useRouter, useRoute } from 'vue-router'
import AuthService from '../services/AuthService/AuthService'
import { onMounted, ref, computed, watch } from 'vue'
import SpinnerLoader from '../components/loaders/SpinnerLoader.vue'
import { User } from '../services/AuthService/types'
import { Establishment } from '../services/EstablishmentService/types'
import {
  MessageTemplate,
  PhoneNumber,
  WhatsappTemplate,
  CreateableTemplateComponent,
  CreateableHeaderComponent,
  CreateableBodyComponent,
  CreateableFooterComponent,
  CreateableButtonsComponent,
  ButtonTypeURL,
  HeaderComponent,
  WhatsappHeaderComponent,
  isWhatsappHeaderComponent,
  TextHeaderParameter,
  isLocationHeaderParameter,
  isVideoHeaderParameter,
  isDocumentHeaderParameter,
  isImageHeaderParameter,
  isTextHeaderParameter
} from '../services/AccountService/types'
// import AccountService from '../services/AccountService/AccountService'
import { POSITION, useToast } from 'vue-toastification'
import EstablishmentService from '@/services/EstablishmentService/EstablishmentService'
import AccountService from '@/services/AccountService/AccountService'
import MetaService from '@/services/MetaService/MetaService'
import BspPageHeader from '@/components/BspPageHeader.vue'
import { AButton, AEmptyViewCard, AIcon, AInput, ASelector, ATag } from '@adereso-ai/vue-components'
import { normalizeSearchTerm } from '@/utils/search'
import type { ProductCatalog } from '@/services/MetaService/types'
import { catalogSendComponent, catalogTemplateError, createCatalogComponents, isCatalogTemplate } from '@/utils/catalogTemplates'
import { useCatalogVisibility } from '@/composables/useCatalogVisibility'
import { useCatalogThumbnail } from '@/composables/useCatalogThumbnail'
import { catalogImageUrl, catalogPreviewLabels, interpolateTemplateText } from '@/utils/catalogPreview'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const loading = ref(true)
const currentUser = ref<User | undefined>(undefined)
const currentEstablishment = ref<Establishment | undefined>(undefined)
// const establishmentName = ref('')
const phoneNumber = ref<PhoneNumber | undefined>(undefined)

const testPhoneNumber = ref('')
const messageTemplateName = ref('')
const messageTemplateLanguage = ref('')
const messageTemplateCategory = ref('')
const messageTemplateType = ref('standard')
const creatingTemplate = ref(false)
const sendingTest = ref(false)
const productCatalogs = ref<ProductCatalog[]>([])
const catalogsLoading = ref(true)
const catalogsError = ref('')
const selectedTestTemplate = ref<MessageTemplate>()
const testCatalogThumbnailSku = ref('')

const templateTypeOptions = [
  { title: 'Standard', value: 'standard' },
  { title: 'Catalog', value: 'catalog' }
]
const isCreatingCatalogTemplate = computed(() => messageTemplateType.value === 'catalog')
const isTestingCatalogTemplate = computed(() => Boolean(selectedTestTemplate.value && isCatalogTemplate(selectedTestTemplate.value)))
const associatedCatalog = computed(() => productCatalogs.value.find(catalog => catalog.is_associated))
const {
  product: thumbnailProduct, products: thumbnailProducts, nextCursor: thumbnailNextCursor,
  loading: thumbnailLoading, loadingMore: thumbnailLoadingMore, error: thumbnailError,
  loadMoreError: thumbnailLoadMoreError, refresh: refreshThumbnail, loadMore: loadMoreThumbnailProducts
} = useCatalogThumbnail(
  computed(() => currentUser.value && isTestingCatalogTemplate.value ? String(route.params.wabaId || '') : ''),
  computed(() => isTestingCatalogTemplate.value ? associatedCatalog.value?.id || '' : ''),
  testCatalogThumbnailSku, MetaService.getInstance
)
const thumbnailProductOptions = computed(() => thumbnailProducts.value.map(product => ({
  title: `${product.name || 'Unnamed product'} · SKU ${product.retailer_id}`, value: product.retailer_id
})))
const selectedThumbnailSku = computed({
  get: () => testCatalogThumbnailSku.value || thumbnailProduct.value?.retailer_id || '',
  set: (value: string | null) => { testCatalogThumbnailSku.value = value || '' }
})
const catalogImageFailed = ref(false)
const catalogThumbnailImageUrl = computed(() => catalogImageUrl(thumbnailProduct.value?.image_url))
watch(catalogThumbnailImageUrl, () => { catalogImageFailed.value = false })
const catalogCardLabels = computed(() => catalogPreviewLabels(selectedTestTemplate.value?.language || 'en', phoneNumber.value?.verified_name || ''))
const catalogReady = computed(() => !catalogsLoading.value && !catalogsError.value && Boolean(associatedCatalog.value) && associatedCatalog.value?.product_count !== 0)
const canSendTest = computed(() => {
  if (sendingTest.value || !testingMessageTemplate.value || !testPhoneNumber.value.trim()) return false
  if (!isTestingCatalogTemplate.value) return true
  return catalogReady.value && Boolean(thumbnailProduct.value?.retailer_id) && !thumbnailLoading.value && !thumbnailError.value &&
    catalogVisible.value === true && !visibilityLoading.value && !visibilitySaving.value &&
    selectedTestTemplate.value?.status === 'APPROVED' &&
    /^\d{7,15}$/.test(testPhoneNumber.value.trim()) &&
    testingMessageTemplate.value.components.every(component => component.type !== 'body' ||
      component.parameters?.every(parameter => 'text' in parameter && Boolean(parameter.text?.trim())))
})

const languageOptions = [
  { title: 'Spanish', value: 'es' },
  { title: 'English', value: 'en' },
  { title: 'French', value: 'fr' }
]

const categoryOptions = [
  { title: 'Authentication', value: 'AUTHENTICATION' },
  { title: 'Marketing', value: 'MARKETING' },
  { title: 'Utility', value: 'UTILITY' }
]

const headerFormatOptions = [
  { title: 'Text', value: 'TEXT' },
  { title: 'Image', value: 'IMAGE' },
  { title: 'Video', value: 'VIDEO' },
  { title: 'Document', value: 'DOCUMENT' },
  { title: 'Location', value: 'LOCATION' }
]

const messageTemplates = ref<Array<MessageTemplate>>([])
const hasCatalogTemplates = computed(() => messageTemplates.value.some(isCatalogTemplate))
const needsCatalogVisibility = computed(() => isCreatingCatalogTemplate.value || isTestingCatalogTemplate.value || hasCatalogTemplates.value)
const {
  visible: catalogVisible, loading: visibilityLoading, saving: visibilitySaving,
  error: visibilityError, refresh: refreshVisibility
} = useCatalogVisibility(
  computed(() => currentUser.value && needsCatalogVisibility.value ? String(route.params.wabaId || '') : ''),
  computed(() => currentUser.value && needsCatalogVisibility.value ? String(route.params.phoneNumberId || '') : ''),
  MetaService.getInstance
)

const templateSearch = ref('')

const testingMessageTemplate = ref<WhatsappTemplate | undefined>(undefined)

const headerComponent = ref<CreateableHeaderComponent | undefined>(undefined)

const bodyComponent = ref<CreateableBodyComponent | undefined>(undefined)

const footerComponent = ref<CreateableFooterComponent | undefined>(undefined)

const buttonsComponent = ref<CreateableButtonsComponent | undefined>(undefined)

const templateBreadcrumbs = computed(() => [
  { title: 'Accounts', to: { name: 'whatsapp-business-accounts' } },
  {
    title: 'Phone number',
    to: {
      name: 'phone-number',
      params: { wabaId: String(route.params.wabaId), phoneNumberId: String(route.params.phoneNumberId) }
    }
  },
  { title: 'Templates', disabled: true }
])

const filteredMessageTemplates = computed(() => {
  const query = normalizeSearchTerm(templateSearch.value)
  if (!query) return messageTemplates.value

  return messageTemplates.value.filter((messageTemplate) =>
    [messageTemplate.name, messageTemplate.language, messageTemplate.status]
      .some((value) => String(value || '').toLowerCase().includes(query))
  )
})

const testTemplateParameterCount = computed(() => {
  return testingMessageTemplate.value?.components.reduce((total, component) => {
    return total + (component.parameters?.length || 0)
  }, 0) || 0
})

const existingComponents = computed(() => {
  if (isCreatingCatalogTemplate.value && bodyComponent.value) {
    return createCatalogComponents(bodyComponent.value, footerComponent.value)
  }
  const components: CreateableTemplateComponent[] = []

  if (headerComponent.value !== undefined) {
    components.push(headerComponent.value)
  }
  if (bodyComponent.value !== undefined) {
    components.push(bodyComponent.value)
  }
  if (footerComponent.value !== undefined) {
    components.push(footerComponent.value)
  }
  if (buttonsComponent.value !== undefined) {
    components.push(buttonsComponent.value)
  }

  return components
})

const catalogCreationError = computed(() => {
  if (!isCreatingCatalogTemplate.value) return undefined
  if (!catalogReady.value) return 'A connected catalog with products is required.'
  return catalogTemplateError({
    name: messageTemplateName.value,
    language: messageTemplateLanguage.value,
    category: messageTemplateCategory.value,
    components: existingComponents.value
  })
})

function onTemplateTypeChange () {
  if (isCreatingCatalogTemplate.value) {
    messageTemplateCategory.value = 'MARKETING'
    headerComponent.value = undefined
    if (!bodyComponent.value) addBodyComponent()
    buttonsComponent.value = { type: 'BUTTONS', buttons: [{ type: 'CATALOG', text: 'View catalog' }] }
  } else if (buttonsComponent.value?.buttons.some(button => button.type === 'CATALOG')) {
    buttonsComponent.value = undefined
  }
}

function numberOfParameters (text: string) {
  const regex = /\{\{(\d+)\}\}/g
  const matches = text.match(regex)
  const numberOfParameters = matches ? new Set(matches).size : 0
  return numberOfParameters
}

function onUrlButtonUrlInput (button: ButtonTypeURL) {
  const numberOfParams = numberOfParameters(button.url)
  if (numberOfParams) {
    button.example = []
    for (let i = 0; i < numberOfParams; i++) {
      button.example.push('')
    }
  } else {
    button.example = null
  }
}

function onBodyTextInput (bodyComponent: CreateableBodyComponent) {
  const numberOfParams = numberOfParameters(bodyComponent.text)
  if (numberOfParams) {
    bodyComponent.example = { body_text: [[]] }
    for (let i = 0; i < numberOfParams; i++) {
      bodyComponent.example.body_text[0].push('')
    }
  } else {
    bodyComponent.example = null
  }
}

function onHeaderTextInput (headerComponent: CreateableHeaderComponent) {
  if (headerComponent.text) {
    const numberOfParams = numberOfParameters(headerComponent.text)
    if (numberOfParams) {
      headerComponent.example = { header_text: [] }
      for (let i = 0; i < numberOfParams; i++) {
        headerComponent.example.header_text.push('')
      }
    } else {
      headerComponent.example = null
    }
  }
}

function selectTestTemplate (messageTemplate: MessageTemplate) {
  selectedTestTemplate.value = messageTemplate
  testCatalogThumbnailSku.value = ''
  testingMessageTemplate.value = {
    name: messageTemplate.name,
    language: { code: messageTemplate.language },
    components: []
  }

  let buttonCounter = 0
  let numberOfParams = 0
  let parameters = []

  for (const component of messageTemplate.components) {
    switch (component.type) {
      case 'HEADER': {
        const header = component as HeaderComponent
        const testHeader = {
          type: 'header',
          text: header.text,
          format: header.format,
          parameters: []
        } as WhatsappHeaderComponent
        const headerParameters: any[] = []
        switch (header.format) {
          case 'TEXT':
            numberOfParams = numberOfParameters(header.text)
            for (let i = 0; i < numberOfParams; i++) {
              headerParameters.push({ type: 'text', text: '', id: i })
            }
            break
          case 'IMAGE':
            headerParameters.push({
              type: 'image',
              image: {
                link: header.example?.header_handle[0] || '',
                default: header.example?.header_handle[0] || ''
              }
            })
            break
          case 'VIDEO':
            headerParameters.push({
              type: 'video',
              video: {
                link: header.example?.header_handle[0] || '',
                default: header.example?.header_handle[0] || ''
              }
            })
            break
          case 'DOCUMENT':
            headerParameters.push({
              type: 'document',
              document: {
                link: header.example?.header_handle[0] || '',
                filename: '',
                default: header.example?.header_handle[0] || ''
              }
            })
            break
          case 'LOCATION':
            headerParameters.push({
              type: 'location',
              location: {
                latitude: '',
                longitude: '',
                name: '',
                address: ''
              }
            })
            break
        }
        testHeader.parameters = headerParameters
        testingMessageTemplate.value.components.push(testHeader)
        break
      }
      case 'BODY':
        numberOfParams = numberOfParameters(component.text)
        parameters = []
        for (let i = 0; i < numberOfParams; i++) {
          parameters.push({ type: 'text', text: '', id: i })
        }
        testingMessageTemplate.value.components.push({
          type: component.type.toLowerCase(),
          text: component.text,
          parameters
        })
        break
      case 'FOOTER':
        testingMessageTemplate.value.components.push({
          type: 'footer',
          text: component.text
        })
        break
      case 'BUTTONS':
        for (const button of component.buttons) {
          switch (button.type) {
            case 'CATALOG':
              testingMessageTemplate.value.components.push({ ...catalogSendComponent(buttonCounter.toString()), text: button.text })
              break
            case 'QUICK_REPLY':
              testingMessageTemplate.value.components.push({
                type: 'button',
                sub_type: 'quick_reply',
                text: button.text,
                index: buttonCounter.toString(),
                parameters: [{ type: 'payload', payload: button.text }]
              })
              break
            case 'PHONE_NUMBER':
              testingMessageTemplate.value.components.push({
                type: 'button',
                sub_type: 'phone_number',
                text: button.text,
                index: buttonCounter.toString(),
                parameters: [{ type: 'phone_number', text: button.text }]
              })
              break
            case 'URL':
              numberOfParams = 0
              numberOfParams = numberOfParameters(button.url)
              parameters = []
              for (let i = 0; i < numberOfParams; i++) {
                parameters.push({ type: 'text', text: '', index: i })
              }
              testingMessageTemplate.value.components.push({
                type: 'button',
                sub_type: 'url',
                url: button.url,
                index: buttonCounter.toString(),
                parameters
              })
              break
            case 'COPY_CODE':
              testingMessageTemplate.value.components.push({
                type: 'button',
                sub_type: 'copy_code',
                text: button.text,
                index: buttonCounter.toString(),
                parameters: [
                  {
                    type: 'coupon_code',
                    coupon_code: button.example[0]
                  }
                ]
              })
              break
          }
          buttonCounter++
        }
        break
    }
  }
}

const failedToLoadImage = ref(false)

function addBodyComponent () {
  bodyComponent.value = { type: 'BODY', text: '' }
}

function addHeaderComponent () {
  headerComponent.value = { type: 'HEADER', text: '', format: 'TEXT' }
}

function addFooterComponent () {
  footerComponent.value = { type: 'FOOTER', text: '' }
}

function addButtonsComponent () {
  buttonsComponent.value = { type: 'BUTTONS', buttons: [] }
}

function removeHeaderComponent () {
  headerComponent.value = undefined
}

function removeBodyComponent () {
  if (isCreatingCatalogTemplate.value) return
  bodyComponent.value = undefined
}

function removeFooterComponent () {
  footerComponent.value = undefined
}

function removeButtonsComponent () {
  if (isCreatingCatalogTemplate.value) return
  buttonsComponent.value = undefined
}

function removeTemplateButton (buttonIndex: number) {
  buttonsComponent.value?.buttons.splice(buttonIndex, 1)
}

function buttonTypeLabel (buttonType: string) {
  const labels: Record<string, string> = {
    QUICK_REPLY: 'Quick reply',
    PHONE_NUMBER: 'Phone number',
    URL: 'Link',
    COPY_CODE: 'Copy code'
  }
  return labels[buttonType] || buttonType
}

function addQuickReplyButton (buttonsComponent: CreateableButtonsComponent) {
  buttonsComponent.buttons.push({ text: '', type: 'QUICK_REPLY' })
}

function addPhoneNumberButton (buttonsComponent: CreateableButtonsComponent) {
  buttonsComponent.buttons.push({ phone_number: '', text: '', type: 'PHONE_NUMBER' })
}

function addUrlrButton (buttonsComponent: CreateableButtonsComponent) {
  buttonsComponent.buttons.push({ url: '', text: '', example: [], type: 'URL' })
}

function addCopyCodeButton (buttonsComponent: CreateableButtonsComponent) {
  buttonsComponent.buttons.push({ example: [''], type: 'COPY_CODE', text: 'Copy offer code' })
}

function validTemplate () {
  if (isCreatingCatalogTemplate.value) return catalogCreationError.value === undefined
  return messageTemplateName.value !== '' && messageTemplateLanguage.value !== '' && existingComponents.value.length > 0 && bodyComponent.value !== undefined
}

async function createMessageTemplate () {
  if (creatingTemplate.value) return
  if (!validTemplate()) {
    toastError(catalogCreationError.value || 'All fields are required')
    return
  }
  creatingTemplate.value = true
  const phoneNumberId = route.params.phoneNumberId as string
  const wabaId = route.params.wabaId as string

  const messageTemplateCreation = {
    name: messageTemplateName.value,
    language: messageTemplateLanguage.value,
    category: messageTemplateCategory.value,
    components: existingComponents.value
  }

  try {
    const accountService = await AccountService.getInstance()
    await accountService.createMessageTemplate(wabaId, phoneNumberId, messageTemplateCreation)
    toastSuccess('Message template created')
    await getMessageTemplates(wabaId, phoneNumberId)
  } catch (error) {
    toastError('Error creating message template: ' + error)
  } finally {
    creatingTemplate.value = false
  }
}

async function deleteMessageTemplate (messageTemplate: MessageTemplate) {
  loading.value = true
  const metaService = await MetaService.getInstance()
  const phoneNumberId = route.params.phoneNumberId as string
  const wabaId = route.params.wabaId as string
  try {
    await metaService.deleteMessageTemplate(wabaId, phoneNumberId, messageTemplate.id, messageTemplate.name)
    toastSuccess('Message template deleted')
    await getMessageTemplates(wabaId, phoneNumberId)
  } catch (error) {
    toastError('Error deleting message template:' + error)
  }
  loading.value = false
}

async function testMessageTemplate () {
  if (sendingTest.value) return
  if (testingMessageTemplate.value === undefined) {
    toastError('Message template is needed to send template')
    return
  }
  if (testPhoneNumber.value === '') {
    toastError('Test phone number is needed to send template')
    return
  }
  if (!canSendTest.value) {
    toastError(isTestingCatalogTemplate.value && catalogVisible.value !== true
      ? 'Confirm that the catalog is visible for this WhatsApp number before sending.'
      : 'Use an approved template, a connected catalog with products, and complete the recipient and body variables.')
    return
  }
  sendingTest.value = true
  const phoneNumberId = route.params.phoneNumberId as string
  const wabaId = route.params.wabaId as string
  const components = testingMessageTemplate.value.components.filter((component) => {
    return component.type !== 'footer' &&
      (component.type !== 'button' ||
      (component.sub_type !== 'phone_number'))
  }).map(component => component.type === 'button' && component.sub_type === 'CATALOG'
    ? catalogSendComponent(component.index, thumbnailProduct.value?.retailer_id || '')
    : component)
  const testTemplate = { ...testingMessageTemplate.value, components }

  try {
    const metaService = await MetaService.getInstance()
    const response = await metaService.testMessageTemplate(
      wabaId, phoneNumberId, testPhoneNumber.value.trim(), testTemplate
    )
    const message = response.messages[0]
    toastSuccess(`Sent message to ${testPhoneNumber.value}. Message ID: ${message.id} (status: ${message.message_status})`)
  } catch (error) {
    toastError('Error testing message template: ' + String(error))
  } finally {
    sendingTest.value = false
  }
}

async function refreshCatalogReadiness () {
  await Promise.allSettled([getProductCatalogs(), refreshVisibility()])
  await refreshThumbnail()
}

async function getProductCatalogs () {
  if (!route.params.wabaId) return
  catalogsLoading.value = true
  catalogsError.value = ''
  try {
    const metaService = await MetaService.getInstance()
    productCatalogs.value = await metaService.getProductCatalogs(String(route.params.wabaId))
  } catch (error) {
    catalogsError.value = `Unable to check the connected catalog: ${error instanceof Error ? error.message : String(error)}`
  } finally {
    catalogsLoading.value = false
  }
}

async function synchronizeMessageTemplates () {
  loading.value = true
  const metaService = await MetaService.getInstance()
  const phoneNumberId = route.params.phoneNumberId as string
  const wabaId = route.params.wabaId as string
  try {
    await metaService.synchronizeMessageTemplates(wabaId, phoneNumberId)
    toastSuccess('Message templates synchronized')
    await getMessageTemplates(wabaId, phoneNumberId)
    await getProductCatalogs()
  } catch (error) {
    toastError('Error synchronizing message templates')
  }
  loading.value = false
}

async function getEstablishment () {
  const establishmentService = await EstablishmentService.getInstance()
  try {
    const establishment = await establishmentService.getCurrentEstablishment()
    currentEstablishment.value = establishment
  } catch (error) {
    // do nothing
  }
}

async function getCurrentUser () {
  const authService = await AuthService.getInstance()
  currentUser.value = authService.currentUser.value
  if (authService.currentUser.value == null) {
    router.push({ name: 'login', query: route.query })
  }
}

async function getphoneNumber (wabaId: string, phoneNumberId: string) {
  try {
    const accountService = await AccountService.getInstance()
    phoneNumber.value = await accountService.getphoneNumber(wabaId, phoneNumberId)
  } catch {
    toastError('Error getting phone number')
  }
}

async function getMessageTemplates (wabaId: string, phoneNumberId: string) {
  try {
    const accountService = await AccountService.getInstance()
    messageTemplates.value = await accountService.getMessageTemplates(wabaId, phoneNumberId)
    if (selectedTestTemplate.value) {
      selectedTestTemplate.value = messageTemplates.value.find(template => template.id === selectedTestTemplate.value?.id)
      if (!selectedTestTemplate.value) testingMessageTemplate.value = undefined
    }
  } catch {
    toastError('Error getting message templates')
  }
}

onMounted(async () => {
  await router.isReady()

  await getCurrentUser()

  await getEstablishment()

  const phoneNumberId = route.params.phoneNumberId as string
  const wabaId = route.params.wabaId as string

  if (phoneNumberId && wabaId) {
    await getphoneNumber(wabaId, phoneNumberId)
    await getMessageTemplates(wabaId, phoneNumberId)
    await getProductCatalogs()
  } else {
    router.push({ name: 'home' })
  }

  loading.value = false
})

function toastError (errorMessage: string) {
  try {
    toast.error(String(errorMessage), {
      position: POSITION.BOTTOM_LEFT
    })
  } catch (error) {
    console.log('error on toast:', error)
  }
}

function toastSuccess (successMessage: string) {
  try {
    toast.success(String(successMessage), {
      position: POSITION.BOTTOM_LEFT
    })
  } catch (error) {
    console.log('error on toast:', error)
  }
}

</script>

<style scoped lang="scss">
.bsp-template-catalog-summary {
  padding: 0 14px 10px;
}

.bsp-template-catalog-summary p {
  margin: 2px 0 0;
  font-size: 13px;
  color: var(--bsp-text-muted);
}

.bsp-catalog-template-notice {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.bsp-catalog-template-notice h2 {
  font-size: 16px;
  margin-bottom: 8px;
}

.bsp-catalog-template-notice p {
  color: var(--bsp-text-muted);
  max-width: 80ch;
  margin: 0;
}

.location-params {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.phone-number {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid black;
  border-radius: 5px;
  padding: 20px;

  .phone-number-info {

    display: flex;
    flex-direction: column;
    border: 1px solid black;
    padding: 20px;
    border-radius: 5px;
    gap: 10px;
    .phone-number-detail {
      .phone-number-name {
        font-size: 20px;
        font-weight: bold;
      }
    }
  }

  .phone-numbers-container {
    display: flex;
    flex-direction: row;
    gap: 10px;

    .phone-number-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;

      .phone-number {
        display: flex;
        flex-direction: row;
        gap: 10px;
        border: 1px solid black;
        padding: 10px;
        border-radius: 5px;
        width: 100%;
      }

      .phone-number:hover {
        scale: 1.01;
        cursor: pointer;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
      }
    }
  }
}

.phone-number-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  gap: 20px;

  .go-back {
    display: flex;
    justify-content: flex-start;
    margin-left: 100px;
    width: 100%;
  }

  .phone-number-loaded {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 10px;
    border-radius: 5px;

    .message-templates-and-creation {
      display: flex;
      gap: 10px;

      .message-template-creator {
        display: flex;
        flex-direction: column;
        gap: 10px;
        border: 1px solid black;
        padding: 20px;
        border-radius: 5px;

        .message-template-creation-form {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
      }
    }

    .message-template-container {
      display: flex;
      gap: 10px;
      flex-direction: column;

      .message-template-list {

        display: flex;
        flex-direction: column;
        gap: 10px;
        height: 600px;
        overflow: auto;
        border-radius: 5px;

        .message-template-test {

          display: flex;
          gap: 10px;

          .message-template-test-input {
            display: flex;
            flex-direction: row;
            gap: 10px;
            align-items: center;
            justify-content: flex-start;
            width: 100%;
          }
        }

        .message-template {
          display: flex;
          flex-direction: column;
          gap: 10px;
          border: 1px solid black;
          padding: 10px;
          border-radius: 5px;
          width: 100%;
          background: rgba(10, 151, 217, 0.2);

          &.selected {
            background: rgba(10, 151, 217, 0.5);
            box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
          }

          &:hover {
            background: rgba(69, 184, 238, 0.5);
          }

          .message-template-actions {
            display: flex;
            flex-direction: row;
            gap: 10px;
          }

          .message-template-name {
            font-size: 20px;
            font-weight: bold;
          }
        }
      }
    }

  .webhooks-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    border: 1px solid black;
    padding: 20px;
    border-radius: 5px;

    .webhooks-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .webhook {
      display: flex;
      flex-direction: row;
      gap: 10px;
      border: 1px solid black;
      padding: 10px;
      border-radius: 5px;
      background: rgba(10, 151, 217, 0.2);

      .webhook-url {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .webhook-enabled {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .delete-webhook {
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-self: center;
      }

    }
  }

  .phone-number-actions {
    display: flex;
    flex-direction: row;
    gap: 10px;
  }
}
}

.message-template-test-selected{
  .media-header{
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;

    .media-header-input{
      display: flex;
      flex-direction: row;
      gap: 8px;
    }

    .image-header-preview {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100px;
      width: 100px;
      border-radius: 2px;
      overflow: hidden;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
  }

}

</style>
